import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import type { Comment } from "@/react/types/Comment";

export interface CommentsConfig {
  canCompose: boolean;
  isCommentsClosed: boolean;
  isCommentsEnabled: boolean;
  isEmbedded: boolean;
  isLikingEnabled: boolean;
  isReadOnly: boolean;
  isReplyingEnabled: boolean;
  onCommentCreated: (comment: Comment) => void;
  onCommentDeleted: (comment: Comment) => void;
  path: string;
  postId: number;
  spaceId: number;
}

const CommentsConfigContext = createContext<CommentsConfig | null>(null);
CommentsConfigContext.displayName = "CommentsConfigContext";

interface CommentsConfigProviderProps extends CommentsConfig {
  children: ReactNode;
}

export const CommentsConfigProvider = ({
  children,
  canCompose,
  isCommentsClosed,
  isCommentsEnabled,
  isEmbedded,
  isLikingEnabled,
  isReadOnly,
  isReplyingEnabled,
  onCommentCreated,
  onCommentDeleted,
  path,
  postId,
  spaceId,
}: CommentsConfigProviderProps) => {
  const value = useMemo<CommentsConfig>(
    () => ({
      canCompose,
      isCommentsClosed,
      isCommentsEnabled,
      isEmbedded,
      isLikingEnabled,
      isReadOnly,
      isReplyingEnabled,
      onCommentCreated,
      onCommentDeleted,
      path,
      postId,
      spaceId,
    }),
    [
      canCompose,
      isCommentsClosed,
      isCommentsEnabled,
      isEmbedded,
      isLikingEnabled,
      isReadOnly,
      isReplyingEnabled,
      onCommentCreated,
      onCommentDeleted,
      path,
      postId,
      spaceId,
    ],
  );

  return (
    <CommentsConfigContext.Provider value={value}>
      {children}
    </CommentsConfigContext.Provider>
  );
};

export const useCommentsConfig = (): CommentsConfig => {
  const value = useContext(CommentsConfigContext);
  if (!value) {
    throw new Error("useCommentsConfig must be used inside Comments.Provider.");
  }
  return value;
};
